import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, isTrustedOAuthMessageOrigin } from '@/lib/api';
import {
  Calendar,
  Folder,
  Mail,
  Table as TableIcon,
  Users,
  Video,
  Chrome,
  Unlink,
  Plus,
  Trash2,
  Shield,
  Server,
  Loader2,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  X,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '../../lib/utils';

export interface GoogleAccountStatus {
  id: string;
  account_label: string | null;
  account_email: string | null;
  configured: boolean;
  authorized: boolean;
  enabled: boolean;
  enabled_scopes: string[];
  token_expiry?: string;
  assigned_user_id?: string | null;
  assigned_user_name?: string | null;
  gmail_shared?: boolean;
  app_published?: boolean;
  connected_at?: string | null;
}

interface GoogleOAuthStatus {
  configured: boolean;
  authorized: boolean;
  enabled: boolean;
  enabled_scopes: string[];
  accounts?: GoogleAccountStatus[];
}

interface TenantUserOption {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface GoogleIntegrationConfigProps {
  configured: boolean;
  templateName: string;
  onIntegrationChanged: () => void;
  onRequestClose: () => void;
}

const TENANT_LEVEL_ASSIGNEE = 'tenant';

// I 3 flussi di condivisione di un account Google, derivati da due campi del
// modello (`assigned_user_id` + `gmail_shared`) ma presentati all'utente come
// un'unica scelta esplicita, per togliere l'ambiguità dei due controlli separati.
type SharingMode = 'personal' | 'personal_shared_email' | 'tenant';

const SHARING_MODES: { mode: SharingMode; title: string; desc: string }[] = [
  {
    mode: 'personal',
    title: 'Personale',
    desc: 'Account e tutti i servizi (Gmail, Calendar, Drive, Sheets, Contacts) riservati a un singolo utente.',
  },
  {
    mode: 'personal_shared_email',
    title: 'Personale, email condivisa',
    desc: "Servizi riservati all'utente; la sola casella Gmail è usabile da tutto il tenant.",
  },
  {
    mode: 'tenant',
    title: 'Tenant (condiviso)',
    desc: 'Account e tutti i servizi disponibili a tutto il tenant e al Workflow Builder.',
  },
];

const SHARING_MODE_TITLE: Record<SharingMode, string> = {
  personal: 'Personale',
  personal_shared_email: 'Personale, email condivisa',
  tenant: 'Tenant (condiviso)',
};

function deriveSharingMode(acct: { assigned_user_id?: string | null; gmail_shared?: boolean }): SharingMode {
  if (!acct.assigned_user_id) return 'tenant';
  return acct.gmail_shared ? 'personal_shared_email' : 'personal';
}

function sharingModeBadgeLabel(acct: GoogleAccountStatus): string {
  const mode = deriveSharingMode(acct);
  if (mode === 'tenant') return 'Tenant · condiviso';
  const who = acct.assigned_user_name || 'utente';
  return mode === 'personal_shared_email' ? `Personale + email · ${who}` : `Personale · ${who}`;
}

function formatUserLabel(u: TenantUserOption): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : u.email;
}

const GOOGLE_SCOPES: Record<string, { name: string; description: string }> = {
  calendar: { name: 'Google Calendar', description: 'Gestione eventi e calendari' },
  drive: { name: 'Google Drive', description: 'File e documenti' },
  gmail: { name: 'Gmail', description: 'Invio e lettura email' },
  sheets: { name: 'Google Sheets', description: 'Fogli di calcolo' },
  contacts: { name: 'Google Contacts', description: 'Rubrica contatti' },
  meet: { name: 'Google Meet', description: 'Link videoconferenze' },
};

function getScopeIcon(scope: string) {
  switch (scope) {
    case 'calendar': return <Calendar className="h-4 w-4" />;
    case 'drive': return <Folder className="h-4 w-4" />;
    case 'gmail': return <Mail className="h-4 w-4" />;
    case 'sheets': return <TableIcon className="h-4 w-4" />;
    case 'contacts': return <Users className="h-4 w-4" />;
    case 'meet': return <Video className="h-4 w-4" />;
    default: return <Server className="h-4 w-4" />;
  }
}

interface TestingExpiryInfo {
  expired: boolean;
  remainingMs: number;
  remainingDays: number;
  remainingHours: number;
  isCritical: boolean;
  label: string;
}

function getTestingExpiryInfo(connectedAt?: string | null): TestingExpiryInfo | null {
  if (!connectedAt) return null;
  const connected = new Date(connectedAt);
  if (isNaN(connected.getTime())) return null;
  const expiryMs = connected.getTime() + 7 * 24 * 60 * 60 * 1000;
  const remainingMs = expiryMs - Date.now();
  const remainingDays = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  const remainingHours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return {
    expired: remainingMs <= 0,
    remainingMs,
    remainingDays,
    remainingHours,
    isCritical: remainingMs > 0 && remainingMs < 2 * 24 * 60 * 60 * 1000,
    label: remainingMs <= 0
      ? 'Scaduto'
      : remainingDays >= 1
        ? `${remainingDays}g ${remainingHours}h`
        : `${remainingHours}h`,
  };
}

function formatConnectedAt(connectedAt?: string | null): string {
  if (!connectedAt) return '—';
  const d = new Date(connectedAt);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function GoogleIntegrationConfig({
  configured,
  templateName,
  onIntegrationChanged,
  onRequestClose,
}: GoogleIntegrationConfigProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isAdminUser = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const [oauthStatus, setOauthStatus] = useState<GoogleOAuthStatus | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUserOption[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['calendar']);
  // Scope scelti per la RE-autorizzazione di un account esistente. Tenuti
  // separati da `selectedScopes` (input del form "Aggiungi account") così che
  // la modifica degli scope in reauth non interferisca con il form di creazione
  // e viceversa. Seminati dagli `enabled_scopes` dell'account all'apertura riga.
  const [reauthScopes, setReauthScopes] = useState<string[]>(['calendar']);
  const [addMode, setAddMode] = useState<SharingMode>('tenant');
  const [addAssigneeUser, setAddAssigneeUser] = useState<string>('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);

  const [saving, setSaving] = useState(false);
  const [startingOAuth, setStartingOAuth] = useState<string | null>(null);
  const [applyingMode, setApplyingMode] = useState<string | null>(null);
  const [draftMode, setDraftMode] = useState<{ id: string; mode: SharingMode; assigneeUser: string } | null>(null);
  const [togglingAppPublished, setTogglingAppPublished] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<string | null>(null);
  const [disconnectingAll, setDisconnectingAll] = useState(false);
  const [pendingDeleteAccount, setPendingDeleteAccount] = useState<{ id: string; email: string } | null>(null);

  const [setupExpanded, setSetupExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const loadOAuthStatus = useCallback(async (): Promise<GoogleOAuthStatus | null> => {
    try {
      const response = await apiClient.get<GoogleOAuthStatus>('/integrations/google/oauth/status');
      if (response.success && response.data) {
        setOauthStatus(response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Error loading Google OAuth status:', error);
    }
    return null;
  }, []);

  const loadTenantUsers = useCallback(async () => {
    if (!isAdminUser) return;
    try {
      const response = await apiClient.get<{ users: TenantUserOption[] }>('/users');
      if (response.success && response.data?.users) {
        setTenantUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error loading tenant users:', error);
    }
  }, [isAdminUser]);

  useEffect(() => {
    void loadOAuthStatus();
    void loadTenantUsers();
  }, [loadOAuthStatus, loadTenantUsers]);

  // OAuth in popup: la SPA non viene mai ricaricata, quindi la sessione resta
  // viva (niente rimbalzo al login). La finestra di consenso notifica l'opener
  // via postMessage; come rete di sicurezza (postMessage non arrivato o finestra
  // chiusa a mano) rinfreschiamo lo stato al rientro del focus sull'opener.
  const oauthPopupRef = useRef<Window | null>(null);
  const oauthWatchCleanupRef = useRef<(() => void) | null>(null);
  // Id monotono del flusso OAuth attivo: il fallback su focus agisce solo se è
  // ancora il flusso corrente, così un timer in volo non tocca un watch successivo.
  const oauthFlowIdRef = useRef<number>(0);
  // Flusso B in CREAZIONE: lo `state` OAuth non porta `gmail_shared`, quindi se
  // l'utente sceglie "Personale, email condivisa" memorizziamo l'intento e lo
  // applichiamo via endpoint sull'account appena creato (diff sugli id pre/post).
  const pendingShareGmailRef = useRef<boolean>(false);
  const prevAccountIdsRef = useRef<Set<string>>(new Set());

  const clearOAuthPopupWatch = useCallback(() => {
    if (oauthWatchCleanupRef.current) {
      oauthWatchCleanupRef.current();
      oauthWatchCleanupRef.current = null;
    }
  }, []);

  const openOAuthPopup = useCallback((): Window | null => {
    const w = 520;
    const h = 660;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(
      'about:blank',
      'google-oauth',
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    oauthPopupRef.current = popup;
    return popup;
  }, []);

  const watchOAuthPopup = useCallback(
    (popup: Window | null) => {
      clearOAuthPopupWatch();
      if (!popup) return;
      // Niente polling di `popup.closed`: la pagina di consenso Google imposta
      // COOP `same-origin` e recide il riferimento opener→popup, quindi ogni
      // accesso a `popup.closed` viene bloccato dal browser e logga warning a
      // ripetizione. Completamento primario: il `postMessage` same-origin dal
      // callback. Rete di sicurezza (postMessage non arrivato o finestra chiusa
      // a mano): al rientro del focus sull'opener — dopo una breve grazia per
      // lasciar arrivare un eventuale postMessage in volo — rinfreschiamo stato
      // e lista parent così un collegamento riuscito non resta visualmente stale.
      const flowId = ++oauthFlowIdRef.current;
      let graceTimer: ReturnType<typeof setTimeout> | null = null;
      const onFocus = () => {
        if (graceTimer) return;
        graceTimer = setTimeout(() => {
          graceTimer = null;
          // Agisci solo se questo è ancora il flusso attivo: un timer in volo non
          // deve toccare un watch avviato nel frattempo né uno già completato dal
          // postMessage (che incrementa l'id via clearOAuthPopupWatch + nuovo watch).
          if (oauthFlowIdRef.current !== flowId || !oauthWatchCleanupRef.current) return;
          clearOAuthPopupWatch();
          setSaving(false);
          setStartingOAuth(null);
          void loadOAuthStatus();
          onIntegrationChanged();
        }, 600);
      };
      window.addEventListener('focus', onFocus);
      oauthWatchCleanupRef.current = () => {
        window.removeEventListener('focus', onFocus);
        if (graceTimer) {
          clearTimeout(graceTimer);
          graceTimer = null;
        }
      };
    },
    [clearOAuthPopupWatch, loadOAuthStatus, onIntegrationChanged],
  );

  const completeOAuthSuccess = useCallback(async () => {
    setShowAddForm(false);
    setClientId('');
    setClientSecret('');
    toast({ title: 'Account Google collegato', description: 'Autorizzazione completata con successo.' });
    const data = await loadOAuthStatus();
    // Flusso B in creazione: applica la condivisione Gmail all'account appena
    // creato (quello assegnato a un utente e non presente prima dell'OAuth).
    if (pendingShareGmailRef.current && data?.accounts) {
      const prev = prevAccountIdsRef.current;
      const created = data.accounts.find((a) => !prev.has(a.id) && !!a.assigned_user_id);
      if (created) {
        try {
          await apiClient.put<{ success: boolean }>(`/integrations/google/${created.id}/gmail-sharing`, { gmail_shared: true });
          await loadOAuthStatus();
        } catch (error) {
          console.error('Error applying Gmail sharing to new account:', error);
        }
      }
    }
    pendingShareGmailRef.current = false;
    prevAccountIdsRef.current = new Set();
    onIntegrationChanged();
  }, [loadOAuthStatus, onIntegrationChanged, toast]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Task #3257 (decisione A3): il callback OAuth resta sul dominio canonico
      // della piattaforma; con la SPA su un dominio prodotto il postMessage
      // arriva cross-origin. Allowlist esplicita: same-origin + origin canonici
      // (apex + app. su BRAND_BASE_DOMAIN), match esatto.
      if (!isTrustedOAuthMessageOrigin(event.origin)) return;
      const data = event.data as { source?: string; status?: string; error?: string } | null;
      if (!data || data.source !== 'google-oauth') return;
      clearOAuthPopupWatch();
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
      oauthPopupRef.current = null;
      setSaving(false);
      setStartingOAuth(null);
      if (data.status === 'success') {
        void completeOAuthSuccess();
      } else {
        toast({ title: 'Autorizzazione non completata', description: data.error || 'Riprova.', variant: 'destructive' });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [clearOAuthPopupWatch, completeOAuthSuccess, toast]);

  useEffect(() => () => clearOAuthPopupWatch(), [clearOAuthPopupWatch]);

  // "Personale, email condivisa" richiede il servizio Gmail: se l'utente lo
  // deseleziona dal picker, ricadiamo su "Personale" per non lasciare uno stato
  // incoerente nel form di creazione.
  useEffect(() => {
    if (addMode === 'personal_shared_email' && !selectedScopes.includes('gmail')) {
      setAddMode('personal');
    }
  }, [selectedScopes, addMode]);

  const accounts = oauthStatus?.accounts ?? [];
  const hasAccounts = accounts.length > 0;
  const isFormState = !hasAccounts || showAddForm;

  const toggleScope = (key: string, checked: boolean) => {
    setSelectedScopes(prev => (checked ? [...prev, key] : prev.filter(s => s !== key)));
  };

  const redirectToOAuth = async (accountId: string | undefined, popup: Window | null, scopes: string[], assignee?: string): Promise<void> => {
    let url = `/integrations/google/oauth/start?scopes=${scopes.join(',')}`;
    if (accountId) {
      url += `&account_id=${accountId}`;
    } else {
      url += `&assigned_user_id=${encodeURIComponent(assignee ?? TENANT_LEVEL_ASSIGNEE)}`;
    }
    const response = await apiClient.get<{ authUrl: string }>(url);
    if (response.success && response.data?.authUrl) {
      if (popup && !popup.closed) {
        popup.location.href = response.data.authUrl;
      } else {
        // Popup bloccato dal browser: fallback al redirect a pagina intera.
        window.location.href = response.data.authUrl;
      }
      return;
    }
    if (popup && !popup.closed) popup.close();
    throw new Error("Impossibile avviare l'autorizzazione OAuth");
  };

  // Unified add-account action: persists credentials (only when new ones are
  // typed or the integration isn't configured yet) and immediately starts the
  // OAuth consent flow in the same click — no save→reopen round-trip.
  const handleSaveAndAuthorize = async () => {
    if (selectedScopes.length === 0) {
      toast({ title: 'Seleziona almeno un servizio', description: 'Devi selezionare almeno un servizio Google da autorizzare', variant: 'destructive' });
      return;
    }
    const needsOwner = addMode === 'personal' || addMode === 'personal_shared_email';
    if (isAdminUser && needsOwner && !addAssigneeUser) {
      toast({ title: 'Seleziona un utente', description: "Scegli l'utente proprietario dell'account per le modalità personali.", variant: 'destructive' });
      return;
    }
    const assignee = isAdminUser && needsOwner ? addAssigneeUser : TENANT_LEVEL_ASSIGNEE;
    const trimmedId = clientId.trim();
    const trimmedSecret = clientSecret.trim();
    const hasNewCreds = !!trimmedId || !!trimmedSecret;
    const mustProvideCreds = !configured || hasNewCreds;
    if (mustProvideCreds && (!trimmedId || !trimmedSecret)) {
      toast({ title: 'Credenziali mancanti', description: 'Inserisci sia Client ID che Client Secret', variant: 'destructive' });
      return;
    }

    // Il popup va aperto in modo sincrono nel gesto di click, prima di qualsiasi
    // await, altrimenti i browser lo bloccano.
    const popup = openOAuthPopup();
    setSaving(true);
    try {
      if (mustProvideCreds) {
        const response = await apiClient.post<{ success: boolean }>('/integrations/google/configure', {
          credentials: { client_id: trimmedId, client_secret: trimmedSecret },
        });
        if (!response.success) {
          if (popup && !popup.closed) popup.close();
          toast({ title: 'Errore', description: response.error || 'Impossibile salvare le credenziali', variant: 'destructive' });
          setSaving(false);
          return;
        }
        onIntegrationChanged();
      }
      // Memorizza l'intento Flusso B + snapshot id account, applicato post-OAuth.
      pendingShareGmailRef.current = isAdminUser && addMode === 'personal_shared_email';
      prevAccountIdsRef.current = new Set(accounts.map((a) => a.id));
      await redirectToOAuth(undefined, popup, selectedScopes, assignee);
      watchOAuthPopup(popup);
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      toast({ title: 'Errore', description: getErrorMessage(error) || "Impossibile avviare l'autorizzazione", variant: 'destructive' });
      setSaving(false);
    }
  };

  const handleReauthorize = async (accountId: string, scopes: string[]) => {
    if (scopes.length === 0) {
      toast({ title: 'Seleziona almeno un servizio', description: 'Devi selezionare almeno un servizio Google da autorizzare', variant: 'destructive' });
      return;
    }
    const popup = openOAuthPopup();
    setStartingOAuth(accountId);
    try {
      await redirectToOAuth(accountId, popup, scopes);
      watchOAuthPopup(popup);
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      toast({ title: 'Errore', description: getErrorMessage(error) || "Impossibile avviare l'autorizzazione", variant: 'destructive' });
      setStartingOAuth(null);
    }
  };

  // Applica un flusso di condivisione (A/B/C) traducendolo nelle due chiamate
  // esistenti (assignee + gmail-sharing), nell'ordine giusto: verso tenant si
  // spegne prima la condivisione Gmail (l'endpoint la rifiuta su account orfani),
  // verso personale si assegna prima il proprietario e poi si allinea la Gmail.
  const applySharingMode = async (acct: GoogleAccountStatus, mode: SharingMode, userId: string | null) => {
    if (mode !== 'tenant' && !userId) {
      toast({ title: 'Seleziona un utente', description: "Scegli l'utente proprietario dell'account.", variant: 'destructive' });
      return;
    }
    const targetAssignee = mode === 'tenant' ? null : userId;
    const desiredShared = mode === 'personal_shared_email';
    const hasGmail = !!acct.enabled_scopes?.includes('gmail');
    const currentAssignee = acct.assigned_user_id ?? null;
    const currentShared = acct.gmail_shared ?? false;

    const putAssignee = async (value: string | null) => {
      const r = await apiClient.put<{ success: boolean }>(`/integrations/google/${acct.id}/assignee`, { assigned_user_id: value });
      if (!r.success) throw new Error(r.error || "Impossibile aggiornare l'assegnatario");
    };
    const putGmailShared = async (value: boolean) => {
      const r = await apiClient.put<{ success: boolean }>(`/integrations/google/${acct.id}/gmail-sharing`, { gmail_shared: value });
      if (!r.success) throw new Error(r.error || 'Impossibile aggiornare la condivisione Gmail');
    };

    setApplyingMode(acct.id);
    try {
      if (mode === 'tenant') {
        if (currentShared) await putGmailShared(false);
        if (currentAssignee !== null) await putAssignee(null);
      } else {
        if (currentAssignee !== targetAssignee) await putAssignee(targetAssignee);
        if (hasGmail && currentShared !== desiredShared) await putGmailShared(desiredShared);
      }
      toast({ title: 'Modalità aggiornata', description: `Account impostato su "${SHARING_MODE_TITLE[mode]}".` });
      setDraftMode(null);
      void loadOAuthStatus();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile aggiornare la modalità', variant: 'destructive' });
    } finally {
      setApplyingMode(null);
    }
  };

  const handleToggleAppPublished = async (accountId: string, currentValue: boolean) => {
    setTogglingAppPublished(accountId);
    try {
      const response = await apiClient.put<{ success: boolean }>(`/integrations/google/${accountId}/app-published`, {
        app_published: !currentValue,
      });
      if (response.success) {
        toast({
          title: !currentValue ? 'App marcata come "In produzione"' : 'App marcata come "In Testing"',
          description: !currentValue
            ? 'Il refresh token non scadrà più automaticamente.'
            : "Attenzione: il refresh token scadrà ogni 7 giorni finché l'app è in modalità Testing.",
        });
        void loadOAuthStatus();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || "Impossibile aggiornare lo stato dell'app", variant: 'destructive' });
    } finally {
      setTogglingAppPublished(null);
    }
  };

  const handleDeleteAccount = async (accountId: string, email: string) => {
    setDeletingAccount(accountId);
    try {
      const response = await apiClient.delete<{ success: boolean }>(`/integrations/google?account_id=${accountId}`);
      if (response.success) {
        toast({ title: 'Account rimosso', description: `L'account ${email || ''} è stato disconnesso` });
        setExpandedRow(prev => (prev === accountId ? null : prev));
        void loadOAuthStatus();
        onIntegrationChanged();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || "Impossibile rimuovere l'account", variant: 'destructive' });
    } finally {
      setDeletingAccount(null);
    }
  };

  const handleDisconnectAll = async () => {
    if (!confirm(`Sei sicuro di voler disconnettere ${templateName}? Le credenziali e il server MCP associato verranno rimossi.`)) {
      return;
    }
    setDisconnectingAll(true);
    try {
      const response = await apiClient.delete<{ success: boolean }>('/integrations/google');
      if (response.success) {
        toast({ title: 'Integrazione disconnessa', description: `${templateName} è stata disconnessa e le credenziali rimosse` });
        onIntegrationChanged();
        onRequestClose();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || "Impossibile disconnettere l'integrazione", variant: 'destructive' });
    } finally {
      setDisconnectingAll(false);
    }
  };

  const openAddForm = () => {
    setClientId('');
    setClientSecret('');
    setSelectedScopes(['calendar']);
    setAddMode('tenant');
    setAddAssigneeUser('');
    setShowAddForm(true);
  };

  const toggleRow = (accountId: string) => {
    const willOpen = expandedRow !== accountId;
    setDraftMode(prev => (prev && prev.id === accountId ? prev : null));
    if (willOpen) {
      const acct = accounts.find(a => a.id === accountId);
      const seed = acct?.enabled_scopes && acct.enabled_scopes.length > 0 ? acct.enabled_scopes : ['calendar'];
      setReauthScopes(seed);
    }
    setExpandedRow(willOpen ? accountId : null);
  };

  const toggleReauthScope = (key: string, checked: boolean) => {
    setReauthScopes(prev => (checked ? [...prev, key] : prev.filter(s => s !== key)));
  };

  const renderSetupGuide = () => (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setSetupExpanded(prev => !prev)}
        className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {setupExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <span className="text-sm font-medium text-foreground">Setup Google Cloud (step-by-step)</span>
        </div>
        <span className="text-xs text-muted-foreground">{setupExpanded ? 'Nascondi' : 'Mostra'}</span>
      </button>
      {setupExpanded && (
        <div className="p-3 space-y-3 bg-card">
          <div className="text-sm">
            <p className="font-medium mb-2 text-foreground">1. Crea credenziali OAuth 2.0</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground text-xs">
              <li>Vai su <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink className="h-3 w-3" /></a></li>
              <li>Crea un progetto se non ne hai uno</li>
              <li>Crea credenziali OAuth 2.0 (Applicazione Web)</li>
              <li>Aggiungi URI di reindirizzamento: <code className="bg-muted px-1 rounded text-xs">https://easyflowai.it/api/integrations/google/oauth/callback</code></li>
            </ol>
          </div>
          <div className="bg-amber-50 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 rounded-lg p-3 text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">2. Abilita le API necessarie</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">Dopo aver creato le credenziali, abilita le API per i servizi che intendi utilizzare:</p>
            <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-300">
              <li><a href="https://console.cloud.google.com/apis/api/gmail.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Gmail API</a> — per email</li>
              <li><a href="https://console.cloud.google.com/apis/api/calendar-json.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Google Calendar API</a> — per calendario</li>
              <li><a href="https://console.cloud.google.com/apis/api/people.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">People API</a> — per contatti</li>
              <li><a href="https://console.cloud.google.com/apis/api/drive.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Google Drive API</a> — per file e documenti</li>
              <li><a href="https://console.cloud.google.com/apis/api/sheets.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Google Sheets API</a> — per fogli di calcolo</li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 rounded-lg p-3 text-sm">
            <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">3. Configura schermata di consenso OAuth</p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Vai su <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">OAuth consent screen <ExternalLink className="h-3 w-3" /></a> per gestire la modalità Testing/Produzione.
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              <strong>Importante:</strong> in modalità Testing i refresh token Google scadono ogni 7 giorni. Per produzione pubblica l'app dopo la verifica.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Selettore unico delle 3 modalità di condivisione come card radio. Usato sia
  // nel form di creazione sia nel pannello di modifica riga, così l'utente fa una
  // scelta esplicita invece di combinare i due controlli separati assignee+gmail.
  const renderSharingModeCards = (
    current: SharingMode,
    hasGmail: boolean,
    onSelect: (mode: SharingMode) => void,
    allowedModes: SharingMode[],
    disabled: boolean,
  ) => (
    <div className="space-y-2">
      {SHARING_MODES.filter((m) => allowedModes.includes(m.mode)).map((m) => {
        const needsGmail = m.mode === 'personal_shared_email';
        const blocked = disabled || (needsGmail && !hasGmail);
        const selected = current === m.mode;
        return (
          <button
            type="button"
            key={m.mode}
            onClick={() => { if (!blocked) onSelect(m.mode); }}
            disabled={blocked}
            className={`w-full text-left p-2.5 border rounded-lg transition-colors ${
              selected
                ? 'bg-blue-50 border-blue-300 dark:bg-blue-950/40 dark:border-blue-800'
                : 'bg-card border-border hover:bg-muted/50'
            } ${blocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className="flex items-start gap-2">
              <span className={`mt-0.5 h-3.5 w-3.5 rounded-full border flex-shrink-0 ${selected ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium">{m.title}</p>
                <p className="text-xs text-muted-foreground">{m.desc}</p>
                {needsGmail && !hasGmail && (
                  <p className="text-[11px] text-amber-600 mt-0.5">Richiede il servizio Gmail tra gli scope.</p>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderScopePicker = (selected: string[], onToggle: (key: string, checked: boolean) => void) => (
    <div>
      <p className="text-sm font-medium text-foreground mb-2">Servizi Google da autorizzare:</p>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(GOOGLE_SCOPES).map(([key, scope]) => {
          const isSelected = selected.includes(key);
          return (
            <label
              key={key}
              className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-blue-50 border-blue-300 dark:bg-blue-950/40 dark:border-blue-800'
                  : 'bg-card border-border hover:bg-muted/50'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onToggle(key, e.target.checked)}
                className="rounded border-input"
              />
              <div className="flex items-center gap-1.5">
                {getScopeIcon(key)}
                <span className="text-sm">{scope.name}</span>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );

  const renderAddForm = () => (
    <div className="space-y-4">
      {showAddForm && hasAccounts && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Aggiungi account Google</p>
          <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
            <X className="h-3.5 w-3.5 mr-1" />
            Annulla
          </Button>
        </div>
      )}

      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1">
            Client ID
            {!configured && <span className="text-red-500">*</span>}
          </Label>
          <div className="relative">
            <Input
              type={showClientId ? 'text' : 'password'}
              placeholder={configured ? '••••••••••••' : 'Client ID OAuth 2.0'}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setShowClientId(prev => !prev)}
            >
              {showClientId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="flex items-center gap-1">
            Client Secret
            {!configured && <span className="text-red-500">*</span>}
          </Label>
          <div className="relative">
            <Input
              type={showClientSecret ? 'text' : 'password'}
              placeholder={configured ? '••••••••••••' : 'Client Secret OAuth 2.0'}
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setShowClientSecret(prev => !prev)}
            >
              {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {configured && (
            <p className="text-xs text-muted-foreground">Lascia vuoti Client ID e Secret per mantenere le credenziali già salvate.</p>
          )}
        </div>
      </div>

      {renderScopePicker(selectedScopes, toggleScope)}

      {isAdminUser && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Modalità di condivisione</Label>
          {renderSharingModeCards(
            addMode,
            selectedScopes.includes('gmail'),
            setAddMode,
            ['personal', 'personal_shared_email', 'tenant'],
            saving,
          )}
          {(addMode === 'personal' || addMode === 'personal_shared_email') && (
            <div className="pt-1">
              <Label className="text-xs font-medium">Utente proprietario</Label>
              <Select value={addAssigneeUser} onValueChange={setAddAssigneeUser}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Seleziona un utente" />
                </SelectTrigger>
                <SelectContent>
                  {tenantUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{formatUserLabel(u)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      <Button className="w-full" onClick={handleSaveAndAuthorize} disabled={saving || selectedScopes.length === 0}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Chrome className="h-4 w-4 mr-2" />}
        Salva e autorizza con Google
      </Button>
    </div>
  );

  const renderRowEditPanel = (acct: GoogleAccountStatus) => {
    const canEditSharing = isAdminUser || (!!currentUser?.id && acct.assigned_user_id === currentUser.id);
    const disabledTooltip = "Solo l'admin o l'utente che ha collegato l'account Google può modificare questa impostazione.";
    const currentMode = deriveSharingMode(acct);
    const rowDraft = draftMode?.id === acct.id ? draftMode : null;
    const draft = rowDraft ? rowDraft.mode : currentMode;
    const hasGmail = !!acct.enabled_scopes?.includes('gmail');
    const allowedModes: SharingMode[] = isAdminUser
      ? ['personal', 'personal_shared_email', 'tenant']
      : ['personal', 'personal_shared_email'];
    const needsOwner = draft === 'personal' || draft === 'personal_shared_email';
    // Il proprietario è scegliibile solo dall'admin; l'utente non-admin resta
    // assegnatario di sé e può cambiare unicamente la condivisione della Gmail.
    const ownerValue = isAdminUser
      ? ((rowDraft?.assigneeUser ?? '') || acct.assigned_user_id || '')
      : (acct.assigned_user_id ?? currentUser?.id ?? '');
    const targetUser = needsOwner ? (ownerValue || null) : null;
    const dirty = draft !== currentMode || (isAdminUser && needsOwner && (acct.assigned_user_id ?? '') !== ownerValue);
    const busy = applyingMode === acct.id;
    const selectMode = (mode: SharingMode) => {
      setDraftMode({ id: acct.id, mode, assigneeUser: acct.assigned_user_id ?? '' });
    };
    return (
      <div className="space-y-3">
        {renderScopePicker(reauthScopes, toggleReauthScope)}
        <Button variant="outline" size="sm" onClick={() => handleReauthorize(acct.id, reauthScopes)} disabled={startingOAuth === acct.id || reauthScopes.length === 0}>
          {startingOAuth === acct.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Riautorizza account
        </Button>

        {canEditSharing ? (
          <div className="space-y-2 pt-2 border-t">
            <span className="text-xs font-medium text-muted-foreground">Modalità di condivisione</span>
            {renderSharingModeCards(draft, hasGmail, selectMode, allowedModes, busy)}
            {isAdminUser && needsOwner && (
              <Select
                value={ownerValue}
                onValueChange={(v) => setDraftMode({ id: acct.id, mode: draft, assigneeUser: v })}
                disabled={busy}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Seleziona un utente" />
                </SelectTrigger>
                <SelectContent>
                  {tenantUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{formatUserLabel(u)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {dirty && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy || (needsOwner && !targetUser)}
                  onClick={() => applySharingMode(acct, draft, targetUser)}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                  Applica
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => setDraftMode(null)}
                >
                  Annulla
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 pt-2 border-t">
            <span className="text-xs text-muted-foreground">Modalità di condivisione</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs font-medium text-foreground cursor-help">{SHARING_MODE_TITLE[currentMode]}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{disabledTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground">App in produzione su Google</span>
          </div>
          <Switch
            checked={acct.app_published || false}
            disabled={togglingAppPublished === acct.id}
            onCheckedChange={() => handleToggleAppPublished(acct.id, acct.app_published || false)}
          />
        </div>
      </div>
    );
  };

  const renderRowDetailPanel = (acct: GoogleAccountStatus) => {
    const expiry = acct.authorized && !acct.app_published ? getTestingExpiryInfo(acct.connected_at) : null;
    return (
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">Stato autorizzazione</span>
          {acct.authorized ? (
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300 font-medium"><CheckCircle className="h-3.5 w-3.5" /> Autorizzato</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-yellow-700 dark:text-yellow-300 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Non autorizzato</span>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <span className="text-muted-foreground">Servizi / scope</span>
          <div className="flex flex-wrap gap-1 justify-end">
            {acct.enabled_scopes && acct.enabled_scopes.length > 0
              ? acct.enabled_scopes.map((s) => (
                  <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0 inline-flex items-center gap-1">
                    {getScopeIcon(s)}
                    {GOOGLE_SCOPES[s]?.name ?? s}
                  </Badge>
                ))
              : <span className="text-muted-foreground">nessuno</span>}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">Modalità di condivisione</span>
          <span className="font-medium text-foreground">{sharingModeBadgeLabel(acct)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">Stato produzione</span>
          {acct.app_published ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-700 border-green-400 bg-green-50 dark:text-green-300 dark:border-green-800 dark:bg-green-950/40">Produzione</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-400 bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:bg-amber-950/40">Testing</Badge>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">Connesso il</span>
          <span className="font-medium text-foreground">{formatConnectedAt(acct.connected_at)}</span>
        </div>
        {expiry && (
          <div className={`mt-1 rounded-md p-2 flex items-start gap-2 ${
            expiry.expired
              ? 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/40 dark:border-red-900 dark:text-red-200'
              : expiry.isCritical
                ? 'bg-orange-50 border border-orange-200 text-orange-800 dark:bg-orange-950/40 dark:border-orange-900 dark:text-orange-200'
                : 'bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-200'
          }`}>
            <Clock className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              {expiry.expired ? (
                <>
                  <p className="font-medium">Refresh token scaduto</p>
                  <p className="text-[11px] mt-0.5">In modalità Testing Google invalida il refresh token dopo 7 giorni. Riautorizza l'account o pubblica l'app.</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Scadenza refresh token: {expiry.label}{expiry.isCritical && ' — riautorizza al più presto'}</p>
                  <p className="text-[11px] mt-0.5">In modalità Testing Google invalida il refresh token dopo 7 giorni dalla connessione. Pubblica l'app o riautorizza prima della scadenza.</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Riepilogo inline della riga account (stile Meta: "N servizi · modalità ·
  // Token: …"). Il pezzo "Token" compare solo quando l'app è in Testing, dove
  // Google invalida il refresh token dopo 7 giorni.
  const renderRowSummary = (acct: GoogleAccountStatus) => {
    const scopeCount = acct.enabled_scopes?.length ?? 0;
    const expiry = acct.authorized && !acct.app_published ? getTestingExpiryInfo(acct.connected_at) : null;
    return (
      <div className="truncate text-xs text-muted-foreground">
        {scopeCount} serviz{scopeCount === 1 ? 'io' : 'i'} · {sharingModeBadgeLabel(acct)}
        {expiry && (
          <span className={expiry.expired || expiry.isCritical ? 'text-orange-600 dark:text-orange-400' : ''}>
            {' · '}Token: {expiry.expired ? 'scaduto' : expiry.label}
          </span>
        )}
      </div>
    );
  };

  // Pannello espanso arioso a 2 colonne: a sinistra il dettaglio (sola lettura),
  // a destra i controlli di gestione (riautorizzazione scope, modalità di
  // condivisione, produzione), sfruttando la larghezza extra del modale.
  const renderExpandedPanel = (acct: GoogleAccountStatus) => (
    <div className="grid grid-cols-1 gap-4 bg-muted/20 px-3 pb-4 pt-3 md:grid-cols-2">
      <div className="space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Dettaglio</p>
        {renderRowDetailPanel(acct)}
      </div>
      <div className="space-y-3 md:border-l md:pl-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Gestione</p>
        {renderRowEditPanel(acct)}
      </div>
    </div>
  );

  const renderAccountsList = () => (
    <div className="space-y-4">
      {/* Toolbar azioni globali (mappata dalla toolbar Meta). */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          {accounts.length} account collegat{accounts.length === 1 ? 'o' : 'i'}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openAddForm}>
            <Plus className="h-4 w-4 mr-1.5" />
            Aggiungi account
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
            onClick={handleDisconnectAll}
            disabled={disconnectingAll}
          >
            {disconnectingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Unlink className="h-4 w-4 mr-1.5" />}
            Disconnetti tutti
          </Button>
        </div>
      </div>

      {/* Accordion righe account (stile Meta). */}
      <div className="overflow-hidden rounded-md border divide-y">
        {accounts.map((acct) => {
          const isExpanded = expandedRow === acct.id;
          const isSelf = !!currentUser?.id && acct.assigned_user_id === currentUser.id;
          return (
            <div key={acct.id}>
              <div className="flex items-center gap-2 pr-2 hover:bg-muted/40">
                <button
                  type="button"
                  onClick={() => toggleRow(acct.id)}
                  className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left"
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="rounded bg-muted p-1.5 shrink-0">
                    <Chrome className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {acct.account_email || acct.account_label || 'Account senza nome'}
                      {isSelf && <span className="ml-1 text-xs font-normal text-muted-foreground">(tu)</span>}
                    </div>
                    {renderRowSummary(acct)}
                  </div>
                  <div className="hidden shrink-0 flex-wrap items-center justify-end gap-1 sm:flex">
                    {acct.authorized ? (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-700 border-green-400 bg-green-50 dark:text-green-300 dark:border-green-800 dark:bg-green-950/40">Autorizzato</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-yellow-700 border-yellow-400 bg-yellow-50 dark:text-yellow-300 dark:border-yellow-800 dark:bg-yellow-950/40">Da autorizzare</Badge>
                    )}
                    {acct.app_published ? (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-700 border-green-400 bg-green-50 dark:text-green-300 dark:border-green-800 dark:bg-green-950/40">Produzione</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-400 bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:bg-amber-950/40">Testing</Badge>
                    )}
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
                  onClick={() => setPendingDeleteAccount({ id: acct.id, email: acct.account_email || '' })}
                  disabled={deletingAccount === acct.id}
                  title="Disconnetti account"
                >
                  {deletingAccount === acct.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {isExpanded && renderExpandedPanel(acct)}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {renderSetupGuide()}
      {isFormState ? renderAddForm() : renderAccountsList()}

      <AlertDialog open={pendingDeleteAccount !== null} onOpenChange={(open) => { if (!open) setPendingDeleteAccount(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere l'account Google?</AlertDialogTitle>
            <AlertDialogDescription>
              {`L'account ${pendingDeleteAccount?.email || 'selezionato'} verrà disconnesso e le relative credenziali eliminate. L'azione è irreversibile.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAccount !== null}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={deletingAccount !== null}
              onClick={(e) => {
                e.preventDefault();
                const target = pendingDeleteAccount;
                if (!target) return;
                void handleDeleteAccount(target.id, target.email).finally(() => setPendingDeleteAccount(null));
              }}
            >
              {deletingAccount !== null ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rimozione…</>
              ) : (
                'Rimuovi'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
